import { Link, createFileRoute } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { WikiAnchorSection } from '@/components/wiki/WikiAnchorSection';
import { WikiToc } from '@/components/wiki/WikiToc';

export const Route = createFileRoute('/jubilant/')({
  component: JubilantPage,
});

function JubilantPage() {
  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'kingdom-state', label: 'Kingdom State' },
    { id: 'province-recovery', label: 'Province Recovery' },
    { id: 'casualties', label: 'Casualty Claiming' },
    { id: 'effects', label: 'Jubilant Effects' },
    { id: 'checklist', label: 'Operator Checklist' },
  ];

  return (
    <div className="space-y-5">
      <WikiToc items={toc} />

      <WikiAnchorSection id="overview" title="Post-War Jubilant">
        <div className="grid gap-4 sm:grid-cols-[120px_1fr]">
          <img
            src="/images/nav/politics@3x.png"
            alt="Jubilant"
            className="w-full max-w-[120px] rounded-xl border border-[#d4af37]/25"
          />
          <div className="space-y-3">
            <p>
              Jubilant is the structured post-war recovery window. It is not just a stance label: it controls
              ceasefire behavior, unlock timing, recovery grants, and when missing war casualties return.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge>Automatic on war end</Badge>
              <Badge>24-day unlock gate</Badge>
              <Badge>48-day hard timeout</Badge>
              <Badge>Intensity-scaled effects</Badge>
            </div>
            <p className="text-sm text-[#b0b0b0]">
              This page reflects legacy engine behavior for the standard withdraw/claim-win war-end flow.
            </p>
          </div>
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="timeline" title="Tick Timeline">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            ['War End (T+0)', 'Both kingdoms are moved into Jubilant, post-war ceasefire is applied, and recovery timers are seeded.'],
            ['First Post-War Tick', 'Province recovery package is applied: land return, resource boosts, science grant, and pending casualty state.'],
            ['Days 1-23', 'Manual Claim War Casualties is available when pending returns exist.'],
            ['Day 24', 'Monarch can switch stance away from Jubilant, and pending casualties begin auto-claiming if still unclaimed.'],
            ['Day 48', 'If still Jubilant, stance auto-resets to Normal on kingdom tick.'],
            ['After Day 48', 'Standard non-war flow resumes; war-side recovery flags remain only as historical state.'],
          ].map(([title, body]) => (
            <Card key={title} className="p-4">
              <h3 className="font-cinzel text-lg text-[#f0f0f0]">{title}</h3>
              <p className="mt-2 text-sm text-[#b0b0b0]">{body}</p>
            </Card>
          ))}
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="kingdom-state" title="Kingdom-Level State Changes">
        <ul className="list-disc space-y-1 pl-5">
          <li>War target fields are cleared and war summary records are finalized.</li>
          <li>`kdstance` is set to Jubilant and `kdstancechange` is set to war-end tick + 24.</li>
          <li>`kdwarstart` is set to war-end tick + 47, which drives the 48-day Jubilant timeout.</li>
          <li>Relations between the two kingdoms are set to ceasefire as part of post-war lockout.</li>
          <li>Jubilant science is credited at kingdom level using averaged land and war intensity.</li>
          <li>Forced-war and revenge-war fields are reset/adjusted for next-cycle eligibility.</li>
        </ul>
      </WikiAnchorSection>

      <WikiAnchorSection id="province-recovery" title="Province Recovery Package">
        <p>
          On the first out-of-war recovery pass, each province receives a one-time Jubilant package. Most values
          scale by war intensity.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Land return is calculated from war damage via `JubAcres(...)`, then applied as barren land.</li>
          <li>Chest storage increases by five slots (one level), capped at the maximum.</li>
          <li>Army credits and soldiers are boosted from returned acres and intensity.</li>
          <li>Mana and stealth are refilled by a percentage of missing points based on intensity.</li>
          <li>Science books are granted as a post-war burst using intensity and kingdom average land.</li>
          <li>Peasants are rebalanced toward living space, with a floor of 1 peasant.</li>
          <li>Food is granted from bushel consumption baseline and intensity.</li>
          <li>Negative spell/op effects and plague are cleared when intensity is above zero.</li>
        </ul>
        <p className="text-sm text-[#b0b0b0]">
          Forced-war losers also get additional honour catch-up logic on this pass if conditions are met.
        </p>
      </WikiAnchorSection>

      <WikiAnchorSection id="casualties" title="Casualty Return: Manual vs Automatic">
        <p>
          During war, entry snapshots are tracked for wizards, thieves, elites, offspecs, and defspecs. Post-war,
          missing units become a pending casualty pool.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Pending casualty state is marked by `entryjub = 99999`.</li>
          <li>Manual claim is available while casualties are pending via the `claimjub` action.</li>
          <li>
            Returned unit amounts are calculated from snapshot deltas and scaled by kingdom war intensity.
          </li>
          <li>If you do not claim early, auto-claim starts once day 24 conditions are reached.</li>
          <li>Any remaining pending casualties are auto-claimed when Jubilant ends.</li>
          <li>Peasants are adjusted during claim, so poor space planning can cause overpopulation pressure.</li>
        </ul>
        <p className="text-sm text-[#b0b0b0]">
          Training during Jubilant updates entry baselines so claim values represent true missing war casualties.
        </p>
      </WikiAnchorSection>

      <WikiAnchorSection id="effects" title="Jubilant Gameplay Effects">
        <p>
          Jubilant formulas are driven by `jubeffect = kdintensity * stanceJubilant`. This means low-intensity wars
          yield softer bonuses, while high-intensity wars produce stronger recovery effects.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Birth rate increases with `jubeffect`.</li>
          <li>Military wages, draft cost, construction cost, raze cost, and food consumption are reduced by `jubeffect`.</li>
          <li>Training time and construction time are reduced by 50% of `jubeffect`.</li>
          <li>Barren population support increases while in Jubilant stance.</li>
          <li>Explore soldier and gold costs are increased by Jubilant war-effect scaling.</li>
          <li>Intra-kingdom attacks are blocked while in Jubilant or Rebuild stance.</li>
          <li>Advisor/chest swap cooldown rules are relaxed during Jubilant.</li>
          <li>Science recycle penalties are removed while in Jubilant/Rebuild.</li>
          <li>Acres aid is enabled during Jubilant under sender/target size constraints.</li>
        </ul>
      </WikiAnchorSection>

      <WikiAnchorSection id="checklist" title="Post-War Operator Checklist">
        <ol className="list-decimal space-y-1 pl-5">
          <li>Confirm kingdom war end status, ceasefire, and Jubilant timer alignment.</li>
          <li>Review province war panel deltas and decide whether to claim casualties early.</li>
          <li>Spend early Jubilant resources intentionally (build, draft, science, aid routing).</li>
          <li>Track day-24 stance decision and avoid drifting into accidental auto-expiry plans.</li>
          <li>Before day 48, verify all key provinces have resolved pending casualty claims.</li>
        </ol>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { to: '/kingdom-relations', label: 'Kingdom Relations' },
            { to: '/trade', label: 'Trade & Aid' },
            { to: '/actions/withdraw_from_war', label: 'Action: Withdraw from War' },
            { to: '/actions/capitulate', label: 'Action: Capitulate War' },
            { to: '/actions/set_kingdom_stance', label: 'Action: Set Kingdom Stance' },
            { to: '/wiki/politics', label: 'Politics Hub' },
          ].map(item => (
            <Link key={item.to} to={item.to}>
              <Card className="p-4 transition-colors hover:border-[#d4af37]/40">
                <h3 className="font-cinzel text-lg text-[#f0f0f0]">{item.label}</h3>
              </Card>
            </Link>
          ))}
        </div>
      </WikiAnchorSection>
    </div>
  );
}
